import { axios } from '@/api/axios.ts';
import { createQueryKeys } from '@lukemorales/query-key-factory';
import type {
  TClient,
  TClientsAttributeDetail,
  TClientsParams,
  TCreateClient,
  TGetClientsResponse,
} from './clients.types.ts';
import type { TUser } from '../auth/auth.types.ts';
import type { TClientMaintenanceTask } from '@/services/client-maintenance-tasks/client-maintenance-task.types.ts';

export const clientsApi = {
  getClients: async (params?: TClientsParams) => {
    const response = await axios.get<TGetClientsResponse>('clients', { params });

    return response.data;
  },
  getClient: async (id: number) => {
    const response = await axios.get<TClient>(`clients/${id}`);

    return response.data;
  },
  getClientsAttributeDetails: async (id: number) => {
    const response = await axios.get<TClientsAttributeDetail[]>(`clients/${id}/attribute-details`);

    return response.data;
  },
  getClientsMaintenanceTasks: async (id: number) => {
    const response = await axios.get<TClientMaintenanceTask[]>(`clients/${id}/maintenance-tasks`);

    return response.data;
  },
  createClient: async (client: TCreateClient) => {
    const response = await axios.post<TUser>('clients', client);

    return response.data;
  },
  updateClient: async (payload: { id: number; client: Partial<TUser> }) => {
    const { id, client } = payload;
    const response = await axios.patch<TUser>(`clients/${id}`, client);

    return response.data;
  },
  deleteClient: async (payload: { id: number }) => {
    const { id } = payload;

    const response = await axios.delete<TClient>(`clients/${id}`);

    return response.data;
  },
  clientsForgotPassword: async (id: number) => {
    const response = await axios.post<void>(`clients/${id}/password/forgot`);

    return response.data;
  },
  clientsResetPassword: async (id: number, password: string) => {
    const response = await axios.post<void>(`clients/${id}/password/reset`, password);

    return response.data;
  },
  updatePropertyImageCover: async (payload: { id: number; property_image_cover: string }) => {
    const { id, property_image_cover } = payload;
    const response = await axios.patch<void>(`clients/${id}/property-image-cover`, {
      property_image_cover,
    });

    return response.data;
  },
};

export const clientsQueries = createQueryKeys('clients', {
  getClients: (params?: TClientsParams) => ({
    queryKey: ['list', params],
    queryFn: () => clientsApi.getClients(params),
  }),
  getClient: (id: number) => ({
    queryKey: ['client', id],
    queryFn: () => clientsApi.getClient(id),
  }),
  getClientsAttributeDetails: (id: number) => ({
    queryKey: ['client-attribute-details', id],
    queryFn: () => clientsApi.getClientsAttributeDetails(id),
  }),
  getClientsMaintenanceTasks: (id: number) => ({
    queryKey: ['client-maintenance-tasks', id],
    queryFn: () => clientsApi.getClientsMaintenanceTasks(id),
  }),
});
